/**
 * MyProgramBuilder.jsx — Créateur "Avancé" : le client garde le même éditeur complet que le
 * coach (types de séries, supersets/drop-sets/tempo, groupe musculaire auto, recherche vidéo
 * YouTube, tonnage cible) mais présenté comme un ASSISTANT guidé par étapes (Infos → Séances →
 * Exercices/séance → Récap), au lieu d'un unique formulaire dense — même standard de fluidité
 * que CustomBeginnerFlow.jsx, sans jamais sacrifier la puissance de l'éditeur avancé.
 */
import React, { useState, useEffect, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Dumbbell, Plus, Trash2, Pencil, Loader2, Check, X, Youtube, Search, Link2, Unlink,
  Target, Clock, Layers, ChevronLeft, ChevronRight, UserCog, Lock, Bot, BookOpen,
  ClipboardList, Sparkles, RotateCcw,
} from 'lucide-react';
import { toast } from 'sonner';
import { useClientAuth } from '../../../context/ClientAuthContext';
import { LibraryPickerModal } from './LibraryPickerModal';
import { estimateSessionMinutes } from './sessionTimeEstimator';
import { computeAutoTempo, TEMPO_HINT } from './tempoEngine';
import { ProgramCoherenceScore } from './ProgramCoherenceScore';

const REST_PRESETS = [30, 45, 60, 90, 120, 180];
const LEVELS = ['Débutant', 'Intermédiaire', 'Avancé'];
const STEPS = ['info', 'sessions', 'exercises', 'recap'];

const blankExercise = () => ({
  exercise_id: null, name: '', muscle_group: '', set_type: 'straight', group_id: null, reps_scheme: '8-12', is_time_based: false,
  sets_count: 3, rest_seconds: 90, tempo: '', tempo_auto: true, youtube_url: '', notes: '',
});
const blankSession = (n) => ({ session_label: `Séance ${n}`, sessions_target: 12, exercises: [blankExercise()] });
const blankProgram = () => ({ name: '', goal: '', level: 'Intermédiaire', weeks_target: 12, tonnage_growth_target_pct: 5, sessions: [blankSession(1)] });
const newGroupId = () => `g${Date.now().toString(36)}${Math.random().toString(36).slice(2, 6)}`;
const blocksOf = (exercises) => {
  const out = [];
  exercises.forEach((ex, idx) => {
    const last = out[out.length - 1];
    if (ex.group_id && last && last.groupId === ex.group_id) last.indices.push(idx);
    else out.push({ groupId: ex.group_id || null, indices: [idx] });
  });
  return out;
};

// Un exercice reste en mode "auto" tant que tempo_auto n'a jamais été mis à false — ou, pour
// les anciens programmes créés avant ce champ, tant qu'aucun tempo n'y avait déjà été saisi
// (pour ne jamais écraser silencieusement une valeur pro déjà en place ailleurs dans l'app).
const shouldAutoTempo = (ex) => ex.tempo_auto === true || (ex.tempo_auto === undefined && !ex.tempo);
const applyAutoTempo = (c) => {
  c.sessions.forEach((s) => {
    s.exercises.forEach((ex) => {
      if (shouldAutoTempo(ex)) {
        ex.tempo = computeAutoTempo({ level: c.level, goal: c.goal, setType: ex.set_type, isTimeBased: ex.is_time_based });
        ex.tempo_auto = true;
      } else {
        ex.tempo_auto = false;
      }
    });
  });
};

export const MyProgramBuilder = ({ en, onBack, onRequestAdjustment }) => {
  const L = (fr, eng) => (en ? eng : fr);
  const { authFetch } = useClientAuth();

  const [programs, setPrograms] = useState([]);
  const [setTypes, setSetTypes] = useState([]);
  const [library, setLibrary] = useState({ muscle_groups: [], exercises: [] });
  const [editing, setEditing] = useState(null);
  const [step, setStep] = useState('info');
  const [exIdx, setExIdx] = useState(0);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [videoSearch, setVideoSearch] = useState(null);
  const [pickerFor, setPickerFor] = useState(null); // { sIdx, eIdx }

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await authFetch('/api/tracking/programs-pro');
      const data = await res.json();
      setPrograms(data.programs || []);
    } catch { /* noop */ }
    setLoading(false);
  }, [authFetch]);

  useEffect(() => {
    load();
    authFetch('/api/tracking/programs-pro/set-types').then((r) => r.json()).then((d) => setSetTypes(d.set_types || [])).catch(() => {});
    authFetch('/api/tracking/programs-pro/exercise-library').then((r) => r.json()).then(setLibrary).catch(() => {});
  }, [load, authFetch]);

  const byGroup = (group) => library.exercises.filter((e) => !group || e.muscle_group === group);
  const nameToGroup = (name) => library.exercises.find((e) => e.name.toLowerCase() === (name || '').trim().toLowerCase())?.muscle_group;
  const upd = (mut) => setEditing((e) => { const c = JSON.parse(JSON.stringify(e)); mut(c); applyAutoTempo(c); return c; });

  const openNew = () => { setEditing(blankProgram()); setStep('info'); setExIdx(0); };
  const openEdit = (p) => {
    const c = { ...JSON.parse(JSON.stringify(p)), level: p.level || 'Intermédiaire' };
    applyAutoTempo(c);
    setEditing(c);
    setStep('recap');
    setExIdx(0);
  };

  const SOLO_TYPES = setTypes.filter((t) => !t.group);
  const GROUP_TYPES = setTypes.filter((t) => t.group);

  const linkWithNext = (sIdx, eIdx, groupSetType) => {
    upd((c) => {
      const exs = c.sessions[sIdx].exercises;
      const cur = exs[eIdx];
      const gid = cur.group_id || newGroupId();
      cur.group_id = gid; cur.set_type = groupSetType;
      const next = exs[eIdx + 1];
      if (next && !next.group_id) { next.group_id = gid; next.set_type = groupSetType; }
      else { exs.splice(eIdx + 1, 0, { ...blankExercise(), group_id: gid, set_type: groupSetType, rest_seconds: cur.rest_seconds }); }
    });
  };
  const addExerciseToGroup = (sIdx, afterIdx, groupId, groupSetType, restSeconds) => {
    upd((c) => { c.sessions[sIdx].exercises.splice(afterIdx + 1, 0, { ...blankExercise(), group_id: groupId, set_type: groupSetType, rest_seconds: restSeconds }); });
  };
  const unlinkGroup = (sIdx, groupId) => {
    upd((c) => { c.sessions[sIdx].exercises.forEach((ex) => { if (ex.group_id === groupId) { ex.group_id = null; ex.set_type = 'straight'; } }); });
  };
  const setGroupType = (sIdx, groupId, newType) => {
    upd((c) => { c.sessions[sIdx].exercises.forEach((ex) => { if (ex.group_id === groupId) ex.set_type = newType; }); });
  };
  const setGroupRest = (sIdx, groupId, seconds) => {
    upd((c) => { c.sessions[sIdx].exercises.forEach((ex) => { if (ex.group_id === groupId) ex.rest_seconds = seconds; }); });
  };

  const save = async () => {
    if (!editing.name.trim()) { toast.error(L('Le nom du programme est requis.', 'Program name is required.')); setStep('info'); return; }
    if (!editing.goal.trim()) { toast.error(L("L'objectif est requis.", 'Goal is required.')); setStep('info'); return; }
    const payload = JSON.parse(JSON.stringify(editing));
    for (let i = 0; i < payload.sessions.length; i += 1) {
      payload.sessions[i].exercises = payload.sessions[i].exercises.filter((e) => e.name.trim());
      if (!payload.sessions[i].exercises.length) {
        toast.error(L(`La séance "${payload.sessions[i].session_label}" doit avoir au moins un exercice.`, `Session "${payload.sessions[i].session_label}" needs at least one exercise.`));
        setExIdx(i); setStep('exercises');
        return;
      }
    }
    setBusy(true);
    const isNew = !payload.program_id;
    const url = isNew ? '/api/tracking/programs-pro' : `/api/tracking/programs-pro/${payload.program_id}`;
    try {
      const res = await authFetch(url, { method: isNew ? 'POST' : 'PUT', body: JSON.stringify(payload) });
      if (!res.ok) throw new Error();
      setEditing(null);
      await load();
      toast.success(L('Programme enregistré !', 'Program saved!'));
    } catch {
      toast.error(L("Échec de l'enregistrement.", 'Save failed.'));
    } finally {
      setBusy(false);
    }
  };

  const del = async (id) => {
    if (!window.confirm(L('Supprimer définitivement ce programme et tout son historique ?', 'Permanently delete this program and its history?'))) return;
    try {
      await authFetch(`/api/tracking/programs-pro/${id}`, { method: 'DELETE' });
      await load();
      toast.success(L('Programme supprimé.', 'Program deleted.'));
    } catch {
      toast.error(L('Erreur lors de la suppression.', 'Delete failed.'));
    }
  };

  const searchVideo = (sIdx, eIdx, query) => {
    if (!query || query.trim().length < 2) { toast.error(L("Tapez d'abord le nom de l'exercice.", 'Type the exercise name first.')); return; }
    setVideoSearch({ sIdx, eIdx, results: [], loading: true });
    authFetch(`/api/tracking/programs-pro/youtube-search?q=${encodeURIComponent(query)}`)
      .then((r) => r.json())
      .then((d) => setVideoSearch({ sIdx, eIdx, results: d.results || [], loading: false }))
      .catch(() => setVideoSearch({ sIdx, eIdx, results: [], loading: false }));
  };
  const pickVideo = (sIdx, eIdx, url) => { upd((c) => { c.sessions[sIdx].exercises[eIdx].youtube_url = url; }); setVideoSearch(null); };

  const goInfoNext = () => {
    if (!editing.name.trim()) { toast.error(L('Le nom du programme est requis.', 'Program name is required.')); return; }
    if (!editing.goal.trim()) { toast.error(L("L'objectif est requis.", 'Goal is required.')); return; }
    setStep('sessions');
  };
  const goSessionsNext = () => { setStep('exercises'); setExIdx(0); };
  const goExercisesNext = () => {
    const cleaned = editing.sessions[exIdx].exercises.filter((e) => e.name.trim());
    if (!cleaned.length) { toast.error(L('Ajoutez au moins un exercice avec un nom à cette séance.', 'Add at least one named exercise to this session.')); return; }
    const next = JSON.parse(JSON.stringify(editing));
    next.sessions[exIdx].exercises = cleaned;
    applyAutoTempo(next);
    setEditing(next);
    if (exIdx < next.sessions.length - 1) setExIdx((i) => i + 1);
    else setStep('recap');
  };
  const goBackStep = () => {
    if (step === 'info') { setEditing(null); return; }
    if (step === 'sessions') { setStep('info'); return; }
    if (step === 'exercises') {
      if (exIdx > 0) { setExIdx((i) => i - 1); return; }
      setStep('sessions'); return;
    }
    if (step === 'recap') { setStep('exercises'); setExIdx(editing.sessions.length - 1); return; }
  };

  if (loading) return <div className="flex justify-center py-16"><Loader2 className="w-6 h-6 animate-spin text-primary" /></div>;

  // ── Assistant par étapes ────────────────────────────────────────────────
  if (editing) {
    const stepIdx = STEPS.indexOf(step);
    const pct = Math.round(((stepIdx + 1) / STEPS.length) * 100);
    const totalMinutes = editing.sessions.reduce((sum, s) => sum + estimateSessionMinutes(s), 0);

    const renderExerciseFields = (sIdx, eIdx, ex, showTypeRest) => (
      <div key={eIdx} className="space-y-2" data-testid={`my-exercise-row-${sIdx}-${eIdx}`}>
        <div className="flex items-center gap-2 flex-wrap">
          <input value={ex.name}
            onChange={(e) => upd((c) => {
              c.sessions[sIdx].exercises[eIdx].name = e.target.value;
              const g = nameToGroup(e.target.value);
              if (g) c.sessions[sIdx].exercises[eIdx].muscle_group = g;
            })}
            list={`my-exercise-lib-${sIdx}-${eIdx}`}
            placeholder={L("Nom de l'exercice", 'Exercise name')} className="flex-1 min-w-[140px] bg-zinc-800 border border-white/10 rounded-lg px-2.5 py-1.5 text-white text-sm" data-testid={`my-exercise-name-input-${sIdx}-${eIdx}`} />
          <datalist id={`my-exercise-lib-${sIdx}-${eIdx}`}>
            {byGroup(ex.muscle_group).map((lib) => <option key={lib.exercise_ref_id} value={lib.name} />)}
          </datalist>
          <button onClick={() => setPickerFor({ sIdx, eIdx })} className="inline-flex items-center gap-1 px-2 py-1.5 bg-primary/15 text-primary rounded-lg text-xs font-semibold" title={L('Choisir depuis la bibliothèque (avec photos/vidéos)', 'Pick from the library (with photos/videos)')} data-testid={`my-open-library-picker-${sIdx}-${eIdx}`}>
            <BookOpen className="w-3.5 h-3.5" />
          </button>
          <button onClick={() => searchVideo(sIdx, eIdx, ex.name)} className="inline-flex items-center gap-1 px-2 py-1.5 bg-red-600/20 text-red-400 rounded-lg text-xs font-semibold" data-testid={`my-search-video-btn-${sIdx}-${eIdx}`}>
            <Search className="w-3.5 h-3.5" />
          </button>
          {!ex.group_id && (
            <select value="" onChange={(e) => { if (e.target.value) linkWithNext(sIdx, eIdx, e.target.value); }}
              title={L('Lier cet exercice au suivant (superset, bi-set...)', 'Link this exercise to the next (superset, biset...)')}
              className="bg-amber-500/10 border border-amber-500/30 rounded-lg px-2 py-1.5 text-amber-300 text-xs font-semibold" data-testid={`my-link-group-select-${sIdx}-${eIdx}`}>
              <option value="">🔗 {L('Lier...', 'Link...')}</option>
              {GROUP_TYPES.map((t) => <option key={t.value} value={t.value}>{t.label}</option>)}
            </select>
          )}
          <button onClick={() => upd((c) => { c.sessions[sIdx].exercises.splice(eIdx, 1); })} className="p-1.5 text-zinc-500 hover:text-red-400"><X className="w-4 h-4" /></button>
        </div>

        {videoSearch && videoSearch.sIdx === sIdx && videoSearch.eIdx === eIdx && (
          <div className="bg-black/50 border border-red-500/20 rounded-lg p-2 space-y-1.5">
            {videoSearch.loading && <p className="text-xs text-zinc-500 flex items-center gap-2"><Loader2 className="w-3.5 h-3.5 animate-spin" /> {L('Recherche...', 'Searching...')}</p>}
            {!videoSearch.loading && videoSearch.results.length === 0 && <p className="text-xs text-zinc-500">{L('Aucun résultat. Ajoutez un lien manuellement.', 'No results. Add a link manually.')}</p>}
            {videoSearch.results.map((v) => (
              <button key={v.video_id} onClick={() => pickVideo(sIdx, eIdx, v.url)} className="w-full flex items-center gap-2 text-left hover:bg-white/5 rounded p-1.5">
                <img src={v.thumbnail} alt="" className="w-12 h-9 rounded object-contain bg-zinc-900 shrink-0" />
                <span className="text-xs text-zinc-300 line-clamp-2">{v.title}</span>
              </button>
            ))}
            <button onClick={() => setVideoSearch(null)} className="text-xs text-zinc-500">{L('Fermer', 'Close')}</button>
          </div>
        )}

        <div className="grid grid-cols-2 gap-2">
          {showTypeRest && (
            <div>
              <label className="text-[10px] text-zinc-500 flex items-center gap-1"><Layers className="w-3 h-3" /> {L('Type de série', 'Set type')}</label>
              <select value={ex.set_type} onChange={(e) => upd((c) => { c.sessions[sIdx].exercises[eIdx].set_type = e.target.value; })}
                className="w-full bg-zinc-800 border border-white/10 rounded-lg px-2 py-1.5 text-white text-xs">
                {SOLO_TYPES.map((t) => <option key={t.value} value={t.value}>{t.label}</option>)}
              </select>
            </div>
          )}
          <div>
            <label className="text-[10px] text-zinc-500 flex items-center gap-1">
              <Sparkles className="w-3 h-3" /> Tempo {ex.tempo_auto !== false && <span className="text-primary/70 font-normal">({L('auto', 'auto')})</span>}
              {ex.tempo_auto === false && (
                <button type="button" onClick={() => upd((c) => { c.sessions[sIdx].exercises[eIdx].tempo_auto = true; })} title={L('Revenir au calcul automatique', 'Reset to automatic')} className="text-zinc-500 hover:text-primary">
                  <RotateCcw className="w-3 h-3" />
                </button>
              )}
            </label>
            <input value={ex.tempo || ''} onChange={(e) => upd((c) => { c.sessions[sIdx].exercises[eIdx].tempo = e.target.value; c.sessions[sIdx].exercises[eIdx].tempo_auto = false; })}
              placeholder="3-1-2-0" title={TEMPO_HINT} disabled={ex.is_time_based || ex.set_type === 'isometric'}
              className="w-full bg-zinc-800 border border-white/10 rounded-lg px-2 py-1.5 text-white text-xs disabled:opacity-40" data-testid={`my-tempo-input-${sIdx}-${eIdx}`} />
          </div>
          <div>
            <label className="text-[10px] text-zinc-500 flex items-center gap-1">
              {ex.is_time_based ? L('Temps', 'Time') : L('Répétitions', 'Reps')}
              <input type="checkbox" checked={ex.is_time_based} onChange={(e) => upd((c) => { c.sessions[sIdx].exercises[eIdx].is_time_based = e.target.checked; })}
                className="w-3 h-3 accent-primary" data-testid={`time-based-checkbox-${sIdx}-${eIdx}`} title={L('Basé sur le temps (cardio/isométrique)', 'Time-based (cardio/isometric)')} />
            </label>
            <input value={ex.reps_scheme} placeholder={ex.is_time_based ? '30s' : '8-12'} onChange={(e) => upd((c) => { c.sessions[sIdx].exercises[eIdx].reps_scheme = e.target.value; })}
              className="w-full bg-zinc-800 border border-white/10 rounded-lg px-2 py-1.5 text-white text-xs" />
          </div>
          <div>
            <label className="text-[10px] text-zinc-500">{L('Nb séries', 'Sets')}</label>
            <div className="flex items-center gap-1">
              <button onClick={() => upd((c) => { c.sessions[sIdx].exercises[eIdx].sets_count = Math.max(1, c.sessions[sIdx].exercises[eIdx].sets_count - 1); })} className="px-2 py-1.5 bg-zinc-800 border border-white/10 rounded-lg text-white text-xs">−</button>
              <span className="flex-1 text-center text-white text-sm font-bold">{ex.sets_count}</span>
              <button onClick={() => upd((c) => { c.sessions[sIdx].exercises[eIdx].sets_count += 1; })} className="px-2 py-1.5 bg-zinc-800 border border-white/10 rounded-lg text-white text-xs">+</button>
            </div>
          </div>
          {showTypeRest && (
            <div>
              <label className="text-[10px] text-zinc-500 flex items-center gap-1"><Clock className="w-3 h-3" /> {L('Récup', 'Rest')}</label>
              <select value={ex.rest_seconds} onChange={(e) => upd((c) => { c.sessions[sIdx].exercises[eIdx].rest_seconds = +e.target.value; })}
                className="w-full bg-zinc-800 border border-white/10 rounded-lg px-2 py-1.5 text-white text-xs">
                {REST_PRESETS.map((r) => <option key={r} value={r}>{r}s</option>)}
              </select>
            </div>
          )}
        </div>

        <div className="flex items-center gap-2">
          <Link2 className="w-3.5 h-3.5 text-zinc-500 shrink-0" />
          <input value={ex.youtube_url || ''} onChange={(e) => upd((c) => { c.sessions[sIdx].exercises[eIdx].youtube_url = e.target.value; })}
            placeholder={L('Lien YouTube', 'YouTube link')} className="flex-1 bg-zinc-800 border border-white/10 rounded-lg px-2 py-1.5 text-zinc-300 text-xs" />
          {ex.youtube_url && <a href={ex.youtube_url} target="_blank" rel="noreferrer" className="text-red-400"><Youtube className="w-4 h-4" /></a>}
        </div>
      </div>
    );

    const renderExerciseBlocks = (sIdx, exercises) => blocksOf(exercises).map((block) => {
      if (block.indices.length === 1) {
        const eIdx = block.indices[0];
        return (
          <div key={eIdx} className="bg-white/[0.035] backdrop-blur-xl border border-white/10 shadow-[0_0_30px_-12px_rgba(217,70,239,0.25)] rounded-xl p-2.5">
            {renderExerciseFields(sIdx, eIdx, exercises[eIdx], true)}
          </div>
        );
      }
      const groupType = exercises[block.indices[0]].set_type;
      const groupRest = exercises[block.indices[0]].rest_seconds;
      const lastIdx = block.indices[block.indices.length - 1];
      return (
        <div key={block.groupId} className="bg-amber-500/[0.05] border-2 border-amber-500/30 rounded-xl p-2.5 space-y-3" data-testid={`my-group-block-${sIdx}-${block.groupId}`}>
          <div className="flex items-center gap-2 flex-wrap">
            <span className="text-[10px] font-black uppercase tracking-wide px-2 py-1 rounded-full bg-amber-500/20 text-amber-300 inline-flex items-center gap-1">
              <Link2 className="w-3 h-3" /> {block.indices.length} {L('exercices liés', 'linked exercises')}
            </span>
            <select value={groupType} onChange={(e) => setGroupType(sIdx, block.groupId, e.target.value)}
              className="bg-zinc-800 border border-amber-500/30 rounded-lg px-2 py-1.5 text-amber-200 text-xs font-semibold">
              {GROUP_TYPES.map((t) => <option key={t.value} value={t.value}>{t.label}</option>)}
            </select>
            <select value={groupRest} onChange={(e) => setGroupRest(sIdx, block.groupId, +e.target.value)}
              className="bg-zinc-800 border border-white/10 rounded-lg px-2 py-1 text-white text-xs">
              {REST_PRESETS.map((r) => <option key={r} value={r}>{r}s</option>)}
            </select>
            <button onClick={() => unlinkGroup(sIdx, block.groupId)} className="ml-auto inline-flex items-center gap-1 text-xs text-zinc-400 hover:text-white">
              <Unlink className="w-3.5 h-3.5" /> {L('Dissocier', 'Unlink')}
            </button>
          </div>
          {block.indices.map((eIdx, mi) => (
            <div key={eIdx} className={mi > 0 ? 'pt-3 border-t border-amber-500/10' : ''}>
              {renderExerciseFields(sIdx, eIdx, exercises[eIdx], false)}
            </div>
          ))}
          <button onClick={() => addExerciseToGroup(sIdx, lastIdx, block.groupId, groupType, groupRest)} className="text-xs text-amber-300 hover:underline inline-flex items-center gap-1">
            <Plus className="w-3.5 h-3.5" /> {L('Ajouter un exercice à ce bloc', 'Add exercise to this block')}
          </button>
        </div>
      );
    });

    return (
      <div className="space-y-4" data-testid="my-program-form">
        <div className="flex items-center justify-between gap-3">
          <button onClick={goBackStep} className="inline-flex items-center gap-1.5 text-zinc-400 hover:text-white text-sm" data-testid="my-wizard-back-btn">
            <ChevronLeft className="w-4 h-4" /> {L('Retour', 'Back')}
          </button>
          <span className="text-xs font-mono text-zinc-500" data-testid="my-wizard-step-counter">
            {stepIdx + 1}/{STEPS.length}{step === 'exercises' ? ` · ${L('Séance', 'Session')} ${exIdx + 1}/${editing.sessions.length}` : ''}
          </span>
        </div>
        <div className="h-2 bg-white/10 rounded-full overflow-hidden">
          <motion.div className="h-full rounded-full bg-gradient-to-r from-primary to-pink-500" animate={{ width: `${pct}%` }} transition={{ duration: 0.3 }} />
        </div>

        <AnimatePresence mode="wait">
          <motion.div key={step === 'exercises' ? `exercises-${exIdx}` : step} initial={{ opacity: 0, x: 16 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -16 }} transition={{ duration: 0.2 }} className="space-y-4">

            {step === 'info' && (
              <div data-testid="my-wizard-step-info" className="space-y-4">
                <h3 className="text-xl font-black text-white">{L('Infos du programme', 'Program info')}</h3>
                <div>
                  <label className="text-xs text-zinc-500 mb-1 block">{L('Nom du programme', 'Program name')}</label>
                  <input value={editing.name} onChange={(e) => upd((c) => { c.name = e.target.value; })}
                    className="w-full bg-zinc-800 border border-white/10 rounded-lg px-3 py-2.5 text-white text-sm" placeholder={L('Ex: Force & Hypertrophie', 'Ex: Strength & Hypertrophy')} data-testid="my-program-name-input" />
                </div>
                <div>
                  <label className="text-xs text-zinc-500 mb-1 block">{L('Objectif du programme', 'Program goal')}</label>
                  <input value={editing.goal} onChange={(e) => upd((c) => { c.goal = e.target.value; })}
                    className="w-full bg-zinc-800 border border-white/10 rounded-lg px-3 py-2.5 text-white text-sm" placeholder={L('Ex: Prise de muscle', 'Ex: Muscle gain')} data-testid="my-program-goal-input" />
                </div>
                <div>
                  <label className="text-xs text-zinc-500 mb-1.5 block">{L('Niveau', 'Level')}</label>
                  <div className="grid grid-cols-3 gap-2">
                    {LEVELS.map((l) => (
                      <button key={l} onClick={() => upd((c) => { c.level = l; })} data-testid={`my-level-${l.toLowerCase()}`}
                        className={`py-2.5 rounded-xl text-xs font-bold border transition-colors ${editing.level === l ? 'bg-primary/20 border-primary/50 text-primary' : 'bg-white/[0.04] border-white/10 text-zinc-400'}`}>
                        {l}
                      </button>
                    ))}
                  </div>
                  <p className="text-[11px] text-zinc-500 mt-1.5 flex items-center gap-1"><Sparkles className="w-3 h-3" /> {L('Détermine le tempo conseillé automatiquement pour chaque exercice (toujours modifiable).', 'Automatically drives the suggested tempo for each exercise (always editable).')}</p>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="text-xs text-zinc-500 mb-1 block">{L('Durée (semaines)', 'Duration (weeks)')}</label>
                    <input type="number" min={1} max={104} value={editing.weeks_target} onChange={(e) => upd((c) => { c.weeks_target = +e.target.value; })}
                      className="w-full bg-zinc-800 border border-white/10 rounded-lg px-3 py-2.5 text-white text-sm" />
                  </div>
                  <div>
                    <label className="text-xs text-zinc-500 mb-1 block">{L('Progression tonnage (%)', 'Tonnage growth (%)')}</label>
                    <input type="number" min={0} max={100} step={0.5} value={editing.tonnage_growth_target_pct} onChange={(e) => upd((c) => { c.tonnage_growth_target_pct = +e.target.value; })}
                      className="w-full bg-zinc-800 border border-white/10 rounded-lg px-3 py-2.5 text-white text-sm" />
                  </div>
                </div>
                <button onClick={goInfoNext} className="w-full py-3.5 rounded-full font-bold text-white flex items-center justify-center gap-2 transition-transform active:scale-95 bg-gradient-to-r from-primary to-pink-500" data-testid="my-wizard-info-next-btn">
                  {L('Continuer', 'Continue')} <ChevronRight className="w-4 h-4" />
                </button>
              </div>
            )}

            {step === 'sessions' && (
              <div data-testid="my-wizard-step-sessions" className="space-y-4">
                <h3 className="text-xl font-black text-white">{L('Vos séances', 'Your sessions')}</h3>
                <p className="text-xs text-zinc-500">{L("Une carte par type de séance (ex: Push, Pull, Jambes...). Vous ajouterez les exercices à l'étape suivante.", 'One card per session type (ex: Push, Pull, Legs...). You will add exercises in the next step.')}</p>
                {editing.sessions.map((s, sIdx) => (
                  <div key={sIdx} className="border border-white/10 rounded-2xl p-3.5 bg-black/30 flex items-center gap-2 flex-wrap" data-testid={`my-session-card-${sIdx}`}>
                    <input value={s.session_label} onChange={(e) => upd((c) => { c.sessions[sIdx].session_label = e.target.value; })}
                      className="flex-1 min-w-[120px] bg-zinc-800 border border-white/10 rounded-lg px-3 py-1.5 text-white text-sm font-bold" data-testid={`my-session-label-input-${sIdx}`} />
                    <label className="text-[11px] text-zinc-500 flex items-center gap-1">
                      <Target className="w-3.5 h-3.5" />
                      <input type="number" min={1} max={300} value={s.sessions_target} onChange={(e) => upd((c) => { c.sessions[sIdx].sessions_target = +e.target.value; })}
                        className="w-14 bg-zinc-800 border border-white/10 rounded-lg px-2 py-1 text-white text-sm" />
                    </label>
                    {editing.sessions.length > 1 && (
                      <button onClick={() => upd((c) => { c.sessions.splice(sIdx, 1); })} className="text-red-400 p-1" data-testid={`my-remove-session-${sIdx}`}><Trash2 className="w-4 h-4" /></button>
                    )}
                  </div>
                ))}
                <button onClick={() => upd((c) => { c.sessions.push(blankSession(c.sessions.length + 1)); })} className="text-sm text-primary inline-flex items-center gap-1.5" data-testid="my-add-session-btn">
                  <Plus className="w-4 h-4" /> {L('Ajouter une séance', 'Add session')}
                </button>
                <button onClick={goSessionsNext} className="w-full py-3.5 rounded-full font-bold text-white flex items-center justify-center gap-2 transition-transform active:scale-95 bg-gradient-to-r from-primary to-pink-500" data-testid="my-wizard-sessions-next-btn">
                  {L('Continuer', 'Continue')} <ChevronRight className="w-4 h-4" />
                </button>
              </div>
            )}

            {step === 'exercises' && editing.sessions[exIdx] && (
              <div data-testid="my-wizard-step-exercises" className="space-y-4">
                <div className="flex items-center justify-between gap-2 flex-wrap">
                  <h3 className="font-black text-white text-lg">{editing.sessions[exIdx].session_label}</h3>
                  <span className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-primary/15 border border-primary/30 text-primary text-xs font-bold" data-testid={`my-session-time-badge-${exIdx}`}>
                    <Clock className="w-3.5 h-3.5" /> ~{estimateSessionMinutes(editing.sessions[exIdx])} min
                  </span>
                </div>
                <div className="flex items-center gap-1.5 overflow-x-auto pb-1">
                  {editing.sessions.map((sess, i) => (
                    <button key={i} onClick={() => setExIdx(i)} data-testid={`my-session-tab-${i}`}
                      className={`shrink-0 px-3 py-1.5 rounded-full text-xs font-bold border transition-colors ${i === exIdx ? 'bg-primary/20 border-primary/50 text-primary' : 'bg-white/5 border-white/10 text-zinc-400'}`}>
                      {sess.session_label}
                    </button>
                  ))}
                </div>
                <div className="space-y-2.5">
                  {renderExerciseBlocks(exIdx, editing.sessions[exIdx].exercises)}
                </div>
                <button onClick={() => upd((c) => { c.sessions[exIdx].exercises.push(blankExercise()); })} className="text-xs text-primary inline-flex items-center gap-1" data-testid="my-add-exercise-btn">
                  <Plus className="w-3.5 h-3.5" /> {L('Ajouter un exercice', 'Add exercise')}
                </button>
                <button onClick={goExercisesNext} className="w-full py-3.5 rounded-full font-bold text-white flex items-center justify-center gap-2 transition-transform active:scale-95 bg-gradient-to-r from-primary to-pink-500" data-testid="my-wizard-exercises-next-btn">
                  {exIdx < editing.sessions.length - 1 ? L('Séance suivante', 'Next session') : L('Voir le récapitulatif', 'See summary')} <ChevronRight className="w-4 h-4" />
                </button>
              </div>
            )}

            {step === 'recap' && (
              <div data-testid="my-wizard-step-recap" className="space-y-4">
                <h3 className="text-xl font-black text-white flex items-center gap-2">
                  <ClipboardList className="w-5 h-5 text-primary" /> {L('Récapitulatif', 'Summary')}
                </h3>
                <div className="rounded-2xl bg-white/[0.04] border border-white/10 p-4 space-y-2 text-sm" data-testid="my-recap-info-card">
                  <div className="flex items-center justify-between">
                    <p className="text-zinc-400">{L('Programme', 'Program')} : <span className="text-white font-semibold">{editing.name || '—'}</span></p>
                    <button onClick={() => setStep('info')} className="text-primary p-1" data-testid="my-recap-edit-info-btn"><Pencil className="w-3.5 h-3.5" /></button>
                  </div>
                  <p className="text-zinc-400">{L('Objectif', 'Goal')} : <span className="text-white font-semibold">{editing.goal || '—'}</span></p>
                  <p className="text-zinc-400">{L('Niveau', 'Level')} : <span className="text-white font-semibold">{editing.level || '—'}</span></p>
                  <p className="text-zinc-400">{L('Durée', 'Duration')} : <span className="text-white font-semibold">{editing.weeks_target} {L('semaines', 'weeks')}</span> · {L('Progression tonnage', 'Tonnage growth')} {editing.tonnage_growth_target_pct}%</p>
                </div>
                <div className="rounded-2xl bg-gradient-to-r from-primary/15 to-pink-500/15 border border-primary/30 p-4 flex items-center justify-between" data-testid="my-recap-total-time-badge">
                  <span className="text-sm font-bold text-white flex items-center gap-2"><Clock className="w-4 h-4 text-primary" /> {L('Temps total (toutes les séances)', 'Total time (all sessions)')}</span>
                  <span className="text-lg font-black text-primary">~{totalMinutes} min</span>
                </div>
                <ProgramCoherenceScore sessions={editing.sessions} en={en} />
                <div className="space-y-2">
                  {editing.sessions.map((s, i) => (
                    <div key={i} className="flex items-center justify-between gap-2 bg-zinc-800/60 border border-white/10 rounded-xl px-3.5 py-2.5" data-testid={`my-recap-session-${i}`}>
                      <div className="min-w-0">
                        <p className="text-white font-semibold text-sm truncate">{s.session_label}</p>
                        <p className="text-xs text-zinc-500">{s.exercises.filter((e) => e.name.trim()).length} {L('exercice(s)', 'exercise(s)')} · ~{estimateSessionMinutes(s)} min</p>
                      </div>
                      <button onClick={() => { setExIdx(i); setStep('exercises'); }} className="p-2 text-zinc-400 hover:text-primary shrink-0" data-testid={`my-recap-edit-session-${i}`}><Pencil className="w-4 h-4" /></button>
                    </div>
                  ))}
                  <button onClick={() => upd((c) => { c.sessions.push(blankSession(c.sessions.length + 1)); })} className="text-sm text-primary inline-flex items-center gap-1.5" data-testid="my-recap-add-session-btn">
                    <Plus className="w-4 h-4" /> {L('Ajouter une séance', 'Add session')}
                  </button>
                </div>
                <button onClick={save} disabled={busy} className="w-full inline-flex items-center justify-center gap-2 px-4 py-3.5 bg-gradient-to-r from-primary to-pink-500 text-white font-bold rounded-full disabled:opacity-50" data-testid="save-my-program-btn">
                  {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Check className="w-4 h-4" />} {L('Enregistrer', 'Save')}
                </button>
              </div>
            )}

          </motion.div>
        </AnimatePresence>

        {pickerFor && (
          <LibraryPickerModal
            en={en}
            onClose={() => setPickerFor(null)}
            onAdd={(picked) => {
              upd((c) => {
                const target = c.sessions[pickerFor.sIdx].exercises[pickerFor.eIdx];
                target.name = picked.name;
                target.muscle_group = picked.muscle_group;
              });
              setPickerFor(null);
            }}
          />
        )}
      </div>
    );
  }

  // ── Liste des programmes ──────────────────────────────────────────────────
  return (
    <div className="space-y-4" data-testid="my-program-builder">
      <button onClick={onBack} className="inline-flex items-center gap-1.5 text-zinc-400 hover:text-white text-sm">
        <ChevronLeft className="w-4 h-4" /> {L('Retour', 'Back')}
      </button>
      <div className="flex items-center justify-between gap-2 flex-wrap">
        <h3 className="font-black text-white text-lg flex items-center gap-2"><UserCog className="w-5 h-5 text-primary" /> {L('Mes programmes', 'My programs')}</h3>
        <button onClick={openNew} className="inline-flex items-center gap-1.5 px-3.5 py-2 bg-gradient-to-r from-primary to-pink-500 text-white text-sm font-bold rounded-xl" data-testid="new-my-program-btn">
          <Plus className="w-4 h-4" /> {L('Nouveau', 'New')}
        </button>
      </div>

      {programs.length === 0 && <p className="text-sm text-zinc-500 py-6 text-center">{L('Aucun programme. Créez le vôtre !', "No programs yet. Create your own!")}</p>}
      <div className="space-y-2">
        {programs.map((p) => (
          <div key={p.program_id} className="flex items-center justify-between gap-3 bg-zinc-800/60 border border-white/10 rounded-xl px-4 py-3" data-testid={`my-program-card-${p.program_id}`}>
            <div className="min-w-0">
              <div className="flex items-center gap-2 flex-wrap">
                <p className="text-white font-semibold text-sm truncate">{p.name}</p>
                {p.author_type === 'client' && <span className="px-1.5 py-0.5 rounded text-[9px] bg-blue-500/15 text-blue-300 border border-blue-500/25 font-bold shrink-0">{L('CRÉÉ PAR VOUS', 'MADE BY YOU')}</span>}
                {p.author_type === 'ai_self' && <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[9px] bg-cyan-500/15 text-cyan-300 border border-cyan-500/25 font-bold shrink-0"><Bot className="w-2.5 h-2.5" /> {L('GÉNÉRÉ PAR IA', 'AI GENERATED')}</span>}
              </div>
              <p className="text-xs text-zinc-500">{p.goal} · {p.sessions.length} {L('séance(s)', 'session(s)')}</p>
            </div>
            <div className="flex items-center gap-1 shrink-0">
              {p.author_type === 'ai_self' ? (
                <button onClick={() => onRequestAdjustment && onRequestAdjustment(p.name)} className="inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg bg-primary/10 border border-primary/25 text-primary text-xs font-semibold hover:bg-primary/20 transition-colors shrink-0" title={L('Verrouillé — demander un ajustement à mon coach', 'Locked — ask your coach for an adjustment')} data-testid={`request-adjustment-btn-${p.program_id}`}>
                  <Lock className="w-3.5 h-3.5" /> {L('Ajuster', 'Adjust')}
                </button>
              ) : (
                <>
                  <button onClick={() => openEdit(p)} className="p-2 text-zinc-400 hover:text-primary" data-testid={`edit-my-program-${p.program_id}`}><Pencil className="w-4 h-4" /></button>
                  <button onClick={() => del(p.program_id)} className="p-2 text-zinc-400 hover:text-red-400" data-testid={`delete-my-program-${p.program_id}`}><Trash2 className="w-4 h-4" /></button>
                </>
              )}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

export default MyProgramBuilder;
